import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import ExpandedChartModal from '../ExpandedChartModal/ExpandedChartModal';
import { useDataContext } from '../../../context/DataContext';
import { IoMdExpand } from 'react-icons/io';
import './ChartsPopup.css';

const ChartsPopup = ({
  isOpen,
  onClose,
  nodeData1,
  nodeData2,
  roadData1,
  roadData2,
  currentScenario,
  onScenarioChange,
  showComparison,
  selectedScenario1,
  selectedScenario2,
  losDataJson,
  alignment = 'right', // 'left' or 'right'
  syncScrollTop = 0,
  onSyncScroll = null,
  onRoadSelect = null, // Callback when a road is selected from dropdown
  sharedExpandedChart = null, // Shared expanded chart state for comparison mode
  onExpandedChartChange = null, // Callback to sync expanded chart state
  onChartDataReady = null, // Callback to expose chart data to parent
  sharedTimeInterval = null, // Shared time interval for comparison mode
  onTimeIntervalChange = null, // Callback to sync time interval changes
  sharedSelectedRoad = null, // Shared selected road for comparison mode
  onSelectedRoadChange = null // Callback to sync selected road changes
}) => {
  const [localTimeInterval, setLocalTimeInterval] = useState('10min');
  const [localSelectedDataType, setLocalSelectedDataType] = useState(''); // Local state for selected road

  // Use shared time interval if provided (comparison mode), otherwise use local state
  const selectedTimeInterval = sharedTimeInterval !== null ? sharedTimeInterval : localTimeInterval;
  const setSelectedTimeInterval = (value) => {
    if (onTimeIntervalChange) {
      // In comparison mode, update the shared state
      onTimeIntervalChange(value);
    } else {
      // In single mode, update local state
      setLocalTimeInterval(value);
    }
  };

  // Use shared selected road if provided (comparison mode), otherwise use local state
  const selectedDataType = sharedSelectedRoad !== null ? sharedSelectedRoad : localSelectedDataType;
  const setSelectedDataType = (value) => {
    if (onSelectedRoadChange) {
      // In comparison mode, update the shared state
      onSelectedRoadChange(value);
    } else {
      // In single mode, update local state
      setLocalSelectedDataType(value);
    }
  };
  const [chartData, setChartData] = useState({
    veicoli: [],
    codaMax: [],
    tempo: [],
    velocita: []
  });
  const [globalIndicators, setGlobalIndicators] = useState({
    veicoli: 0,
    codaMax: 0,
    tempo: 0,
    velocita: 0
  });
  const [expandedChart, setExpandedChart] = useState(null);
  const [scenarioGraficiData, setScenarioGraficiData] = useState(null);
  const [hasDataAvailable, setHasDataAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(true); // Start with loading state
  const [availableIntervals, setAvailableIntervals] = useState(['1sec', '1min', '5min', '10min', '15min', '30min']); // Default fallback
  const [streetNotFound, setStreetNotFound] = useState(false); // Track if selected street doesn't exist in this scenario
  const [missingStreetName, setMissingStreetName] = useState(''); // Name of the missing street

  // Use DataContext for client-specific data (default or lavagna)
  const { dataJson, graficiDataJson, dataLoaded, clientType } = useDataContext();

  // Dragging state - start positioned at 0,0 relative to alignment
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  // Resize state
  const [size, setSize] = useState({ width: 450, height: 550 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0, posX: 0, posY: 0 });

  // Overlay style based on alignment
  const overlayStyle = {
    justifyContent: alignment === 'left' ? 'flex-start' : 'flex-end',
    alignItems: 'flex-start',
    paddingTop: '170px',
    paddingLeft: alignment === 'left' ? '280px' : '0',
    paddingRight: alignment === 'right' ? '50px' : '0'
  };

  // Get current scenario ID
  const currentScenarioId = currentScenario === 1 ? selectedScenario1 : selectedScenario2;

  // Use graficiDataJson from context instead of fetching directly
  useEffect(() => {
    if (dataLoaded && graficiDataJson) {
      setScenarioGraficiData(graficiDataJson);
      setIsLoading(false);
    } else if (!dataLoaded) {
      setIsLoading(true);
    }
  }, [dataLoaded, graficiDataJson]);

  // Extract available time intervals when data is loaded
  useEffect(() => {
    if (scenarioGraficiData?.scenario && scenarioGraficiData.scenario.length > 0) {
      const firstScenario = scenarioGraficiData.scenario[0];
      if (firstScenario.sezioni && firstScenario.sezioni.length > 0) {
        const firstSection = firstScenario.sezioni[0];
        if (firstSection.serieTemporale && firstSection.serieTemporale.inputCount) {
          const keys = Object.keys(firstSection.serieTemporale.inputCount);
          // Sort keys to ensure logical order: 1sec, 1min, 5min, 10min, 15min, 30min...
          const order = ['1sec', '1min', '5min', '10min', '15min', '30min', '1h'];
          const sortedKeys = keys.sort((a, b) => {
            const indexA = order.indexOf(a);
            const indexB = order.indexOf(b);
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return a.localeCompare(b);
          });
          
          if (sortedKeys.length > 0) {
            setAvailableIntervals(sortedKeys);
            // If current selected interval is not in the new list, switch to a default preference
            if (!sortedKeys.includes(selectedTimeInterval)) {
               // Prefer 10min, then 5min, then first available
               if (sortedKeys.includes('10min')) setSelectedTimeInterval('10min');
               else if (sortedKeys.includes('5min')) setSelectedTimeInterval('5min');
               else setSelectedTimeInterval(sortedKeys[0]);
            }
          }
        }
      }
    }
  }, [scenarioGraficiData]);

  // Get current node and road data based on selected scenario
  const currentNodeData = currentScenario === 1
    ? (nodeData1 || (roadData1?.nodeData))
    : (nodeData2 || (roadData2?.nodeData));
  const currentRoadData = currentScenario === 1 ? roadData1 : roadData2;

  // Get all available roads from scenario or node
  const getAvailableRoads = () => {
    if (currentNodeData && currentNodeData.sezioni) {
      return Object.values(currentNodeData.sezioni).map((section, index) => {
        const sectionKey = String(section.idSec).includes('.') ? String(section.idSec) : `${section.idSec}.0`;
        const osmItem = dataJson?.geometryOSM?.find(item => 
          String(item.id) === String(sectionKey) || 
          String(item.id) === String(section.idSec)
        );
        const displayName = (osmItem?.display_name && osmItem.display_name !== null) ? 
          osmItem.display_name : (osmItem?.name || section.nomeSec || `Strada ${index + 1}`);

        return {
          id: `road-${currentNodeData.idNodo}-${index}`,
          nodeId: currentNodeData.idNodo,
          nodeName: currentNodeData.nome || "Intersezione",
          name: displayName,
          los: section.losSec,
          color: section.color,
          data: section
        };
      });
    }

    const scenarioData = losDataJson?.scenario?.find(scenario => scenario.replicaAvg === currentScenarioId);
    if (!scenarioData?.LosNodi) return [];

    const allRoads = [];
    scenarioData.LosNodi.forEach(node => {
      if (node.sezioni) {
        Object.values(node.sezioni).forEach((section, index) => {
          const sectionKey = String(section.idSec).includes('.') ? String(section.idSec) : `${section.idSec}.0`;
          const osmItem = dataJson?.geometryOSM?.find(item => 
            String(item.id) === String(sectionKey) || 
            String(item.id) === String(section.idSec)
          );
          const displayName = (osmItem?.display_name && osmItem.display_name !== null) ? 
            osmItem.display_name : (osmItem?.name || section.nomeSec || `Strada ${index + 1}`);

          allRoads.push({
            id: `road-${node.idNodo}-${index}`,
            nodeId: node.idNodo,
            nodeName: node.nome || "Intersezione",
            name: `${displayName} (${node.nome || "Intersezione"})`,
            los: section.losSec,
            color: section.color,
            data: section,
            nodeData: node
          });
        });
      }
    });

    return allRoads;
  };

  // Update selectedDataType when road selection changes
  useEffect(() => {
    if (currentRoadData?.sectionData) {
      setSelectedDataType('current');
    } else if (currentNodeData) {
      const roads = getAvailableRoads();
      if (roads.length > 0) {
        setSelectedDataType(roads[0].id);
      } else {
        setSelectedDataType('');
      }
    } else {
      // Reset to empty state when both roadData and nodeData are null
      setSelectedDataType('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoadData, currentNodeData]);

  // Handle scenario changes
  useEffect(() => {
    if (currentRoadData?.sectionData || currentNodeData) {
      return;
    }
    if (selectedDataType && selectedDataType.startsWith('road-')) {
      return;
    } else if (!selectedDataType || selectedDataType === '') {
      setSelectedDataType('');
    }
  }, [currentScenarioId, losDataJson]);

  // Get data based on selection type
  const getSelectedData = () => {
    // Check if we have a shared selected road (from comparison mode)
    const isSharedRoadSelection = sharedSelectedRoad !== null && sharedSelectedRoad !== '';

    if (selectedDataType === 'node') {
      const roads = getAvailableRoads();
      if (roads.length > 0) {
        return {
          isNode: false,
          data: roads[0].data,
          nodeData: roads[0].nodeData || currentNodeData,
          roadNotFound: false
        };
      }
    } else if (selectedDataType.startsWith('road-')) {
      const roads = getAvailableRoads();
      const selectedRoad = roads.find(r => r.id === selectedDataType);

      // If this is a shared selection and road not found, mark it
      if (!selectedRoad && isSharedRoadSelection) {
        return {
          isNode: false,
          data: null,
          nodeData: currentNodeData,
          roadNotFound: true,
          searchedRoadId: selectedDataType
        };
      }

      return {
        isNode: false,
        data: selectedRoad?.data,
        nodeData: selectedRoad?.nodeData || currentNodeData,
        roadNotFound: false
      };
    } else {
      if (currentRoadData && currentRoadData.sectionData) {
        return {
          isNode: false,
          data: currentRoadData.sectionData,
          nodeData: currentNodeData,
          roadNotFound: false
        };
      }

      // If currentRoadData exists but sectionData is null, the road was selected
      // from the map but not found in this scenario - show "not found"
      if (currentRoadData && !currentRoadData.sectionData) {
        return {
          isNode: false,
          data: null,
          nodeData: currentNodeData,
          roadNotFound: true,
          searchedRoadId: currentRoadData.osmId || currentRoadData.osmName || selectedDataType
        };
      }

      // If we have a shared selection but no currentRoadData for this scenario,
      // mark as not found instead of falling back to a random road
      if (isSharedRoadSelection) {
        return {
          isNode: false,
          data: null,
          nodeData: currentNodeData,
          roadNotFound: true,
          searchedRoadId: selectedDataType
        };
      }

      // Only fall back to first available road if NOT in shared selection mode
      const roads = getAvailableRoads();
      if (roads.length > 0) {
        return {
          isNode: false,
          data: roads[0].data,
          nodeData: roads[0].nodeData || currentNodeData,
          roadNotFound: false
        };
      }
    }
    return {
      isNode: false,
      data: null,
      nodeData: currentNodeData,
      roadNotFound: false
    };
  };

  // Generate Time Series Data
  useEffect(() => {
    const generateTimeSeriesData = () => {
      setIsLoading(true);
      setStreetNotFound(false); // Reset street not found state
      setMissingStreetName('');
      const selectedData = getSelectedData();

      // Check if the road was not found in the available roads (comparison mode)
      if (selectedData.roadNotFound) {
        setStreetNotFound(true);
        // Don't show the internal road ID, just show a generic message
        setMissingStreetName('');
        setChartData({ veicoli: [], codaMax: [], tempo: [], velocita: [] });
        setGlobalIndicators({ veicoli: 0, codaMax: 0, tempo: 0, velocita: 0 });
        setHasDataAvailable(false);
        setIsLoading(false);
        return;
      }

      // Check if we have a shared selected road that might not exist in this scenario
      const isSharedRoadSelection = sharedSelectedRoad !== null && sharedSelectedRoad !== '';

      if (scenarioGraficiData?.scenario && currentScenarioId) {
        const scenario = scenarioGraficiData.scenario.find(s => s.replicaAvg === currentScenarioId);

        if (scenario?.sezioni) {
          let sectionData = null;
          let searchedStreetName = '';

          if (!selectedData.isNode && selectedData.data) {
            // Convert idSec to number for comparison (handles "455.0" vs 455)
            const targetIdSec = selectedData.data.idSec;
            
            // Resolve display name for the searched street
            const sectionKey = String(targetIdSec).includes('.') ? String(targetIdSec) : `${targetIdSec}.0`;
            const osmItem = dataJson?.geometryOSM?.find(item => 
              String(item.id) === String(sectionKey) || 
              String(item.id) === String(targetIdSec)
            );
            searchedStreetName = (osmItem?.display_name && osmItem.display_name !== null) ? 
              osmItem.display_name : (osmItem?.name || selectedData.data.nomeSec || `Sezione ${targetIdSec}`);
            // Normalize ID: parse to float, then to int if it's a whole number
            const normalizeId = (id) => {
              if (id === null || id === undefined) return null;
              const parsed = typeof id === 'string' ? parseFloat(id) : id;
              return Number.isInteger(parsed) ? parsed : Math.floor(parsed);
            };
            const targetIdSecNorm = normalizeId(targetIdSec);

            sectionData = scenario.sezioni.find(s => {
              // Normalize section ID for comparison
              const sectionIdNorm = normalizeId(s.id);

              // Match only by normalized ID (names can be inaccurate)
              return sectionIdNorm === targetIdSecNorm;
            });

            console.log('Looking for section with ID:', targetIdSec, '(normalized:', targetIdSecNorm, ') | Found:', sectionData?.id);

            // If section not found in scenario_grafici.json, show "street not found" message
            // This happens when the street exists in los_per_scenario.json but not in scenario_grafici.json
            if (!sectionData && targetIdSec) {
              setStreetNotFound(true);
              setMissingStreetName(searchedStreetName);
              setChartData({ veicoli: [], codaMax: [], tempo: [], velocita: [] });
              setGlobalIndicators({ veicoli: 0, codaMax: 0, tempo: 0, velocita: 0 });
              setHasDataAvailable(false);
              setIsLoading(false);
              return;
            }
          } else if (selectedData.nodeData) {
            const firstSectionId = Object.keys(selectedData.nodeData.sezioni || {})[0];
            if (firstSectionId) {
              // Normalize ID: "455.0" -> 455
              const normalizeId = (id) => {
                if (id === null || id === undefined) return null;
                const parsed = typeof id === 'string' ? parseFloat(id) : id;
                return Number.isInteger(parsed) ? parsed : Math.floor(parsed);
              };
              const targetIdNorm = normalizeId(firstSectionId);

              sectionData = scenario.sezioni.find(s => {
                const sectionIdNorm = normalizeId(s.id);
                return sectionIdNorm === targetIdNorm;
              });
            }
          }

          if (sectionData) {
            // Set global indicators
            if (sectionData.indicatoriGlobali) {
              setGlobalIndicators({
                veicoli: sectionData.indicatoriGlobali.totOutputCount || 0,
                codaMax: sectionData.indicatoriGlobali.maxQmax || 0,
                tempo: sectionData.indicatoriGlobali.mediaTtSec || 0,
                velocita: sectionData.indicatoriGlobali.mediaSpSec || 0
              });
            }

            const interval = selectedTimeInterval === '1h' ? '60min' : (selectedTimeInterval === '30min' ? '30min' : selectedTimeInterval);
            // Use selected interval if available, otherwise fallback to first available or 10min
            const timeKey = interval;
            
            const inputData = sectionData.serieTemporale.inputCount?.[timeKey] || sectionData.serieTemporale.inputCount?.['10min'];
            const qmaxData = sectionData.serieTemporale.qmax?.[timeKey] || sectionData.serieTemporale.qmax?.['10min'];
            const speedData = sectionData.serieTemporale.speed?.[timeKey] || sectionData.serieTemporale.speed?.['10min'];
            const ttimeData = sectionData.serieTemporale.ttime?.[timeKey] || sectionData.serieTemporale.ttime?.['10min'];

            const veicoli = [];
            const codaMax = [];
            const tempo = [];
            const velocita = [];

            // Helper to process data points - captures all available vehicle types
            const processDataPoints = (sourceData, targetArray) => {
              if (!sourceData) return;

              // Get all available vehicle type keys (Totale, Car, Truck, BRT, etc.)
              const vehicleTypes = Object.keys(sourceData);
              if (vehicleTypes.length === 0) return;

              // Use the first vehicle type to get time keys
              const firstType = vehicleTypes[0];
              const timeKeys = Object.keys(sourceData[firstType] || {});

              timeKeys.forEach(timeKey => {
                const time = parseFloat(timeKey);
                if (!isNaN(time)) {
                  const point = { time };

                  // Add all vehicle types as separate data keys
                  vehicleTypes.forEach(vType => {
                    const value = sourceData[vType]?.[timeKey];
                    if (value !== undefined && value !== null) {
                      // Use lowercase key for chart dataKey
                      const key = vType.toLowerCase();
                      point[key] = value;
                    }
                  });

                  // Set 'value' as Totale or first available type for backwards compatibility
                  point.value = point.totale ?? point.car ?? point[vehicleTypes[0].toLowerCase()] ?? 0;

                  targetArray.push(point);
                }
              });
            };

            processDataPoints(inputData, veicoli);
            processDataPoints(qmaxData, codaMax);
            processDataPoints(ttimeData, tempo);
            processDataPoints(speedData, velocita);

            const hasAnyData = veicoli.length > 0 || codaMax.length > 0 || tempo.length > 0 || velocita.length > 0;

            if (hasAnyData) {
              setChartData({
                veicoli: veicoli.sort((a, b) => a.time - b.time),
                codaMax: codaMax.sort((a, b) => a.time - b.time),
                tempo: tempo.sort((a, b) => a.time - b.time),
                velocita: velocita.sort((a, b) => a.time - b.time)
              });
              setHasDataAvailable(true);
              setIsLoading(false);
              return;
            }
          }
        }
      }

      setChartData({ veicoli: [], codaMax: [], tempo: [], velocita: [] });
      setGlobalIndicators({ veicoli: 0, codaMax: 0, tempo: 0, velocita: 0 });
      setHasDataAvailable(false);
      setIsLoading(false);
    };

    generateTimeSeriesData();
  }, [currentNodeData, selectedTimeInterval, currentRoadData, selectedDataType, currentScenarioId, losDataJson, scenarioGraficiData, clientType]);

  // Notify parent when chart data is ready
  useEffect(() => {
    if (onChartDataReady) {
      onChartDataReady(chartData);
    }
  }, [chartData, onChartDataReady]);

  // Handle drag
  const handleDragStart = (e) => {
    if (e.target.closest('.charts-popup-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleDragMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleDragEnd = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, dragStart]);

  // Resize handlers
  const minWidth = 320;
  const minHeight = 350;
  const maxWidth = window.innerWidth * 0.9;
  const maxHeight = window.innerHeight * 0.85;

  const handleResizeStart = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      posX: position.x,
      posY: position.y
    });
  };

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeDirection) return;

    const deltaX = e.clientX - resizeStart.x;
    const deltaY = e.clientY - resizeStart.y;

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;

    // Handle horizontal resize - east expands right, west expands left (but position stays fixed)
    if (resizeDirection.includes('e')) {
      newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStart.width + deltaX));
    }
    if (resizeDirection.includes('w')) {
      // When resizing from west, shrink/grow width based on mouse movement
      newWidth = Math.min(maxWidth, Math.max(minWidth, resizeStart.width - deltaX));
    }

    // Handle vertical resize - south expands down, north expands up (but position stays fixed)
    if (resizeDirection.includes('s')) {
      newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStart.height + deltaY));
    }
    if (resizeDirection.includes('n')) {
      // When resizing from north, shrink/grow height based on mouse movement
      newHeight = Math.min(maxHeight, Math.max(minHeight, resizeStart.height - deltaY));
    }

    setSize({ width: newWidth, height: newHeight });
    // Position stays fixed during resize - only changes during drag
  }, [isResizing, resizeDirection, resizeStart, minWidth, minHeight, maxWidth, maxHeight]);

  const handleResizeEnd = () => {
    setIsResizing(false);
    setResizeDirection(null);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove]);

  // Handle scroll synchronization
  const scrollRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  // Apply synchronized scroll top
  useEffect(() => {
    if (scrollRef.current && Math.abs(scrollRef.current.scrollTop - syncScrollTop) > 10) {
      // Mark as programmatic scroll to prevent feedback loop
      isProgrammaticScrollRef.current = true;
      scrollRef.current.scrollTop = syncScrollTop;
      
      // Reset the flag after scroll completes
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 50);
    }
  }, [syncScrollTop]);

  const handleScroll = (e) => {
    // Don't sync if this is a programmatic scroll
    if (isProgrammaticScrollRef.current) {
      return;
    }
    
    if (onSyncScroll) {
      // Clear any pending scroll sync
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Debounce the scroll sync to prevent excessive updates
      scrollTimeoutRef.current = setTimeout(() => {
        onSyncScroll(e.target.scrollTop);
      }, 10);
    }
  };

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  const getLosGrade = () => {
    const selectedData = getSelectedData();
    if (!selectedData.isNode && selectedData.data) return selectedData.data.losSec || 'N/A';
    if (selectedData.nodeData) return selectedData.nodeData.losNode || 'B';
    return 'N/A';
  };

  const getLosColor = (grade) => {
    const colors = {
      'A': '#8FC975',
      'B': '#AED361',
      'C': '#F6ED60',
      'D': '#FDB64E',
      'E': '#EC1D23',
      'F': '#470D16'
    };
    return colors[grade] || '#999';
  };

  const getIntersectionName = () => {
    if (currentNodeData) return currentNodeData.nome || currentNodeData.intersection || 'Intersezione';
    const selectedData = getSelectedData();
    if (selectedData?.nodeData) return selectedData.nodeData.nome || selectedData.nodeData.intersection || 'Intersezione';
    return 'Seleziona una strada';
  };

  // Helper for rendering a single chart card
  const renderChart = (title, data, dataKey, color, unit, type = "monotone", globalValue = null) => {
    // Determine label based on unit
    let label = unit.toUpperCase();
    if (unit === 'veh') label = 'Totale veicoli usciti';
    else if (unit === 'queue') label = 'Coda massima';
    else if (unit === 'sec') label = 'Tempo di percorrenza medio';
    else if (unit === 'km/h') label = 'Velocità media';

    // Check if data has multiple vehicle types
    const hasMultipleTypes = data && data.length > 0 && (
      typeof data[0].car !== 'undefined' ||
      typeof data[0].truck !== 'undefined' ||
      typeof data[0].brt !== 'undefined' ||
      typeof data[0].totale !== 'undefined'
    );

    // Define colors for different vehicle types
    const vehicleTypeColors = {
      totale: '#16A34A',  // Green
      car: '#2563EB',     // Blue
      truck: '#EA580C',   // Orange
      brt: '#9333EA',     // Purple
    };

    // Get available vehicle types from data
    const getAvailableVehicleTypes = () => {
      if (!data || data.length === 0) return [];
      const firstPoint = data[0];
      const types = [];
      if (typeof firstPoint.totale !== 'undefined') types.push({ key: 'totale', name: 'Totale', color: vehicleTypeColors.totale });
      if (typeof firstPoint.car !== 'undefined') types.push({ key: 'car', name: 'Car', color: vehicleTypeColors.car });
      if (typeof firstPoint.truck !== 'undefined') types.push({ key: 'truck', name: 'Truck', color: vehicleTypeColors.truck });
      if (typeof firstPoint.brt !== 'undefined') types.push({ key: 'brt', name: 'BRT', color: vehicleTypeColors.brt });
      return types;
    };

    const availableTypes = getAvailableVehicleTypes();

    return (
    <div className="chart-card">
      <div className="chart-header">
        <h3>{title}</h3>
        <button className="chart-expand-btn" onClick={() => {
          const chartConfig = { title, data, dataKey, color, unit };
          if (onExpandedChartChange) {
            // In comparison mode, use shared state
            onExpandedChartChange(chartConfig);
          } else {
            // In single mode, use local state
            setExpandedChart(chartConfig);
          }
        }}>
          <IoMdExpand />
        </button>
      </div>
      <div style={{ width: '100%', height: hasMultipleTypes && availableTypes.length > 1 ? 170 : 150 }}>
        <ResponsiveContainer>
          <AreaChart data={data}>
            <defs>
              <linearGradient id={`color${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.1}/>
                <stop offset="95%" stopColor={color} stopOpacity={0}/>
              </linearGradient>
              {/* Gradients for each vehicle type */}
              {availableTypes.map(vt => (
                <linearGradient key={`gradient-${vt.key}`} id={`color${vt.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={vt.color} stopOpacity={0.1}/>
                  <stop offset="95%" stopColor={vt.color} stopOpacity={0}/>
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="#e5e5e5" />
            <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#999' }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#999' }} />
            <Tooltip
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              itemStyle={{ color: '#333', fontWeight: 600 }}
              formatter={(value, name) => [Number(value).toFixed(2), name]}
            />
            {/* Render all available vehicle types with legend */}
            {hasMultipleTypes && availableTypes.length > 1 ? (
              <>
                {availableTypes.map(vt => (
                  <Area
                    key={vt.key}
                    type={type}
                    dataKey={vt.key}
                    name={vt.name}
                    stroke={vt.color}
                    fillOpacity={0}
                    strokeWidth={2}
                  />
                ))}
                <Legend
                  verticalAlign="bottom"
                  height={28}
                  iconType="plainline"
                  wrapperStyle={{ bottom: -5, fontSize: '11px', fontWeight: 600 }}
                />
              </>
            ) : (
              <Area
                type={type}
                dataKey="value"
                stroke={color}
                fillOpacity={1}
                fill={`url(#color${dataKey})`}
                strokeWidth={2}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-footer">
        <span className="chart-metric-label">{label}</span>
        <span className="chart-metric-value">
          {globalValue !== null ? Number(globalValue).toFixed(2) : (
            data && data.length > 0
              ? (data.reduce((acc, curr) => acc + (curr.value || 0), 0) / data.length).toFixed(2)
              : "0.00"
          )}
        </span>
      </div>
    </div>
  );
  };

  // Get the current expanded chart (from shared state in comparison mode, or local state)
  const currentExpandedChart = sharedExpandedChart !== null ? sharedExpandedChart : expandedChart;
  const handleCloseExpandedChart = () => {
    if (onExpandedChartChange) {
      onExpandedChartChange(null);
    } else {
      setExpandedChart(null);
    }
  };

  return (
    <>
      {/* Expanded Chart Modal */}
      {currentExpandedChart && (
        <ExpandedChartModal
          isOpen={!!currentExpandedChart}
          onClose={handleCloseExpandedChart}
          chartData={currentExpandedChart.data}
          chartConfig={{
            color: currentExpandedChart.color,
            gradientId: currentExpandedChart.dataKey
          }}
          title={currentExpandedChart.title}
          footer={
            <span>
              {currentExpandedChart.unit === 'veh' && 'Totale veicoli usciti'}
              {currentExpandedChart.unit === 'queue' && 'Coda massima'}
              {currentExpandedChart.unit === 'sec' && 'Tempo di percorrenza medio'}
              {currentExpandedChart.unit === 'km/h' && 'Velocità media'}
            </span>
          }
          showTimeSelector={true}
          currentTimeInterval={selectedTimeInterval}
          onTimeChange={setSelectedTimeInterval}
        />
      )}

      <div className="charts-popup-overlay" style={overlayStyle}>
        <div
          className={`charts-popup ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''}`}
          ref={modalRef}
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: isDragging || isResizing ? 'none' : 'transform 0.2s ease',
            width: `${size.width}px`,
            height: `${size.height}px`
          }}
          onMouseDown={handleDragStart}
        >
          {/* Resize handles */}
          <div className="resize-handle resize-n" onMouseDown={(e) => handleResizeStart(e, 'n')} />
          <div className="resize-handle resize-s" onMouseDown={(e) => handleResizeStart(e, 's')} />
          <div className="resize-handle resize-e" onMouseDown={(e) => handleResizeStart(e, 'e')} />
          <div className="resize-handle resize-w" onMouseDown={(e) => handleResizeStart(e, 'w')} />
          <div className="resize-handle resize-ne" onMouseDown={(e) => handleResizeStart(e, 'ne')} />
          <div className="resize-handle resize-nw" onMouseDown={(e) => handleResizeStart(e, 'nw')} />
          <div className="resize-handle resize-se" onMouseDown={(e) => handleResizeStart(e, 'se')} />
          <div className="resize-handle resize-sw" onMouseDown={(e) => handleResizeStart(e, 'sw')} />

          <div className="charts-popup-header">
            <div className="charts-popup-title">
              {selectedDataType && (
                <button 
                  className="charts-back-btn" 
                  onClick={() => setSelectedDataType('')}
                  title="Torna alla selezione strada"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7"/>
                  </svg>
                </button>
              )}
              <h2>Analisi Sezione</h2>
            </div>
            <div style={{ display: "flex" }}>
              <button className="charts-popup-close" onClick={onClose}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          <div className="charts-popup-content" ref={scrollRef} onScroll={handleScroll}>
            {isLoading ? (
              <div className="charts-loading-state">
                <div className="loading-spinner">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                    <path d="M12 2 A10 10 0 0 1 22 12" strokeLinecap="round">
                      <animateTransform
                        attributeName="transform"
                        type="rotate"
                        from="0 12 12"
                        to="360 12 12"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    </path>
                  </svg>
                </div>
                <h3>Caricamento dati...</h3>
                <p>Attendere il caricamento dei grafici.</p>
              </div>
            ) : !currentScenarioId ? (
              <div className="charts-empty-state">
                <div className="empty-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="9" y1="9" x2="15" y2="9" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                  </svg>
                </div>
                <h3>Nessuno scenario selezionato</h3>
                <p>Seleziona uno scenario per visualizzare i dati e i grafici.</p>
              </div>
            ) : (!currentNodeData && !currentRoadData && !selectedDataType) ? (
              <div className="charts-empty-state">
                <div className="empty-icon">
                   <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                </div>
                <h3>Seleziona una strada</h3>
                <p style={{ marginBottom: '20px' }}>Seleziona una strada dal menu sotto per visualizzare i grafici dettagliati.</p>
                {(() => {
                  const availableRoads = getAvailableRoads();
                  if (availableRoads.length > 0) {
                    return (
                      <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto' }}>
                        <select
                          className="road-selector-dropdown"
                          value={selectedDataType}
                          onChange={(e) => {
                            const roadId = e.target.value;
                            setSelectedDataType(roadId);
                            
                            // Call onRoadSelect callback to highlight the street on the map
                            if (onRoadSelect && roadId) {
                              const roads = getAvailableRoads();
                              const selectedRoad = roads.find(r => r.id === roadId);
                              if (selectedRoad && selectedRoad.data) {
                                // Find the OSM ID from the section data
                                // idSec format is "12345.0" - extract just the base ID "12345"
                                const idSec = selectedRoad.data.idSec;
                                const osmId = idSec ? String(idSec).split('.')[0] : null;
                                if (osmId) {
                                  onRoadSelect(`osm-${osmId}`, {
                                    osmId: osmId,
                                    osmName: selectedRoad.name,
                                    sectionData: selectedRoad.data
                                  });
                                }
                              }
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid #ddd',
                            fontSize: '14px',
                            cursor: 'pointer',
                            backgroundColor: 'white'
                          }}
                        >
                          <option value="">-- Seleziona una strada --</option>
                          {availableRoads.map((road) => (
                            <option key={road.id} value={road.id}>
                              {road.name} (LOS: {road.los})
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                  return <p style={{ marginTop: '16px', color: '#999' }}>Nessuna strada disponibile per questo scenario.</p>;
                })()}
              </div>
            ) : (selectedDataType && selectedDataType !== '') || currentRoadData ? (
              <>
                 <div className='charts-street-section'>
                  <div className="street-info">
                    {(() => {
                      const selectedData = getSelectedData();
                      const nodeData = selectedData?.nodeData || currentNodeData;
                      if (nodeData && nodeData.tipo) {
                        return (
                          <div className="vehicle-type-info">
                            <span className="vehicle-type-label">Tipo di intersezione:</span>
                            <span className="vehicle-type-value">
                              {nodeData.tipo === 'rotatoria' ? 'Rotatoria' :
                                nodeData.tipo === 'semaforo' ? 'Semaforo' : nodeData.tipo}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  <div className="los-indicator-container">
                    <div className="los-current-grade">
                      <span className="los-current-label">LOS Grade:</span>
                      <span
                        className="los-current-value"
                        style={{
                          backgroundColor: getLosColor(getLosGrade()),
                          color: ['C', 'D'].includes(getLosGrade()) ? '#333' : 'white'
                        }}
                      >
                        {getLosGrade()}
                      </span>
                    </div>
                    <div className="los-gradient-bar">
                      <div className="los-gradient" />
                      <div className="los-labels">
                        {['A', 'B', 'C', 'D', 'E', 'F'].map((grade, index) => (
                          <span
                            key={grade}
                            className={`los-label ${getLosGrade() === grade ? 'active' : ''}`}
                            style={{ left: `${(index * 16.67) + 8.33}%` }}
                          >
                            {grade}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scenario switcher - only show if NOT in split mode (which is not handled by checking alignment, but if we are here we render normally) 
                    Actually, if we split, we pass one scenario. If onScenarioChange is no-op, buttons do nothing.
                    But user said "split this window". If split, we probably don't need switcher.
                    However, maintaining logic: */}
                {showComparison && selectedScenario1 && selectedScenario2 && (
                  <div className="charts-scenario-switcher">
                    <button
                      className={`scenario-tab ${currentScenario === 1 ? 'active' : ''}`}
                      onClick={() => onScenarioChange(1)}
                    >
                      Scenario 1
                    </button>
                    <button
                      className={`scenario-tab ${currentScenario === 2 ? 'active' : ''}`}
                      onClick={() => onScenarioChange(2)}
                    >
                      Scenario 2
                    </button>
                  </div>
                )}
                
                {/* Time Interval Selector (Global for all charts) */}
                 <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                   <div style={{position: 'relative'}}>
                    <select 
                      className="time-selector"
                      value={selectedTimeInterval}
                      onChange={(e) => setSelectedTimeInterval(e.target.value)}
                    >
                      {availableIntervals.map(interval => (
                        <option key={interval} value={interval}>
                          {interval === '1h' ? '1 ora' : 
                           interval === '30min' ? '30 min' : 
                           interval === '15min' ? '15 min' : 
                           interval === '10min' ? '10 min' : 
                           interval === '5min' ? '5 min' : 
                           interval === '1min' ? '1 min' :
                           interval === '1sec' ? '1 sec' : interval}
                        </option>
                      ))}
                    </select>
                   </div>
                 </div>

                {streetNotFound ? (
                  <div className="charts-no-data charts-street-not-found">
                    <div className="no-data-icon">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                    </div>
                    <h3>Strada non presente</h3>
                    <p>La strada selezionata{missingStreetName ? ` "${missingStreetName}"` : ''} non esiste in questo scenario.</p>
                    <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>Seleziona una strada diversa o cambia scenario.</p>
                  </div>
                ) : !hasDataAvailable ? (
                  <div className="charts-no-data">
                    <div className="no-data-icon">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" />
                      </svg>
                    </div>
                    <h3>Dati non disponibili</h3>
                    <p>Non ci sono dati per lo scenario selezionato.</p>
                  </div>
                ) : (
                  <div className="charts-grid">
                    {renderChart("Veicoli", chartData.veicoli, "veicoli", "#10B981", "veh", "stepAfter", globalIndicators.veicoli)}
                    {renderChart("Coda Max (Veicoli)", chartData.codaMax, "codaMax", "#3B82F6", "queue", "stepAfter", globalIndicators.codaMax)}
                    {renderChart("Tempo Medio (s)", chartData.tempo, "tempo", "#F59E0B", "sec", "stepAfter", globalIndicators.tempo)}
                    {renderChart("Velocità Media (km/h)", chartData.velocita, "velocita", "#6366F1", "km/h", "stepAfter", globalIndicators.velocita)}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChartsPopup;
